import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Menu, X, LayoutDashboard, LogOut, Shield } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Home" },
  { to: "/packages", label: "Packages" },
  { to: "/#devices", label: "Devices" },
  { to: "/#faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 200);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <div className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label.toLowerCase()}`}
              onClick={() => go(l.to)}
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white rounded-lg hover:bg-white/5 transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-3">
          {user && user.role ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="user-menu-btn" className="border-white/15 bg-white/5 text-white hover:bg-white/10">
                  {user.name?.split(" ")[0] || "Account"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-slate-950 border-white/10 text-slate-200">
                {user.role === "admin" ? (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer">
                    <Shield className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")} className="cursor-pointer">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); navigate("/"); }} className="cursor-pointer text-red-400">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")} className="text-slate-200 hover:text-white hover:bg-white/5">
                Login
              </Button>
              <Button data-testid="nav-register-btn" onClick={() => navigate("/register")} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
                Get Started
              </Button>
            </>
          )}
        </div>
        <button data-testid="mobile-menu-toggle" className="lg:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </nav>
      {open && (
        <div className="lg:hidden glass border-t border-white/10 px-4 py-4 space-y-2">
          {links.map((l) => (
            <button key={l.label} data-testid={`mobile-nav-${l.label.toLowerCase()}`} onClick={() => go(l.to)} className="block w-full text-left px-4 py-2 text-slate-200 hover:bg-white/5 rounded-lg">
              {l.label}
            </button>
          ))}
          <div className="pt-2 border-t border-white/10 space-y-2">
            {user && user.role ? (
              <>
                <Button className="w-full" variant="outline" onClick={() => go(user.role === "admin" ? "/admin" : "/dashboard")}>
                  {user.role === "admin" ? "Admin Panel" : "Dashboard"}
                </Button>
                <Button className="w-full bg-red-600/80 text-white" onClick={() => { logout(); go("/"); }}>Logout</Button>
              </>
            ) : (
              <>
                <Button className="w-full" variant="outline" onClick={() => go("/login")}>Login</Button>
                <Button className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white" onClick={() => go("/register")}>Get Started</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
